import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Alert,
  Container,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { getVehiclesForBuyerShowcase } from "../../stores/VeihcleService";
import userService from "../../stores/UserService";
import BuyerAppHeader from "../../components/comprador/BuyerAppHeader";
import VehicleDetailsModal from "../../components/dashboard/vehicles/VehiclesDetail";

const placeholderImage =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="240" viewBox="0 0 400 240"><rect fill="#e0e0e0" width="400" height="240"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#757575" font-family="sans-serif" font-size="16">Sem foto</text></svg>`,
  );

const BuyerVitrinePage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const list = await getVehiclesForBuyerShowcase();
        if (!cancelled) {
          setVehicles(list);
          console.log("Veículos carregados:", list);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError("Não foi possível carregar os veículos. Tente novamente.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleOpenVehicle = async (vehicle) => {
    let selected = {
      ...vehicle,
      photos: {
        ...(vehicle.photos || {}),
        front:
          vehicle?.photos?.front && String(vehicle.photos.front).trim() !== ""
            ? vehicle.photos.front
            : placeholderImage,
      },
    };

    if (!selected.user && selected.userId) {
      try {
        const user = await userService.getUserByUid(selected.userId);
        if (user) {
          selected = { ...selected, user };
          if (!selected.city && user?.city) selected.city = user.city;
          if (!selected.state && user?.state) selected.state = user.state;
        }
      } catch (err) {
        console.warn("Erro ao buscar dados do usuário do veículo:", err);
      }
    }

    setSelectedVehicle(selected);
    setVehicleModalOpen(true);
  };

  const handleCloseVehicle = () => {
    setVehicleModalOpen(false);
    setSelectedVehicle(null);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        pb: 6,
      }}
    >
      <BuyerAppHeader />
      <Container
        maxWidth="lg"
        sx={{ pt: { xs: 10, sm: 12 }, px: { xs: 2, sm: 3, md: 4 } }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            fontWeight="700"
            gutterBottom
            sx={{
              background: "linear-gradient(135deg, #F49841 0%, #F2852A 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Vitrine de Veículos
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: "1rem" }}
          >
            Explore nossa seleção de veículos de qualidade
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : vehicles.length === 0 ? (
          <Typography color="text.secondary">
            Nenhum veículo ativo no momento.
          </Typography>
        ) : (
          <Grid
            container
            spacing={{ xs: 2, sm: 3, md: 3, lg: 3 }}
            sx={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(3, 1fr)",
                xl: "repeat(3, 1fr)",
              },
            }}
          >
            {vehicles.map((vehicle) => {
              const brandName =
                typeof vehicle?.brand === "object"
                  ? (vehicle?.brand?.nome ?? "")
                  : (vehicle?.brand ?? "");
              const model = vehicle?.model ?? "";
              const year = vehicle?.year ?? "";
              const modelYear = vehicle?.modelYear ?? "";
              const mileage = vehicle?.mileage ?? null;
              const city = vehicle?.city ?? "";
              const state = vehicle?.state ?? "";
              const askingPrice = vehicle?.askingPrice ?? "";

              const imageSrc =
                vehicle?.photos?.front &&
                String(vehicle.photos.front).trim() !== ""
                  ? vehicle.photos.front
                  : placeholderImage;

              return (
                <Box key={vehicle.id}>
                  <Card
                    elevation={0}
                    onClick={() => handleOpenVehicle(vehicle)}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "12px",
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "rgba(255, 255, 255, 0.8)",
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(10px)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow:
                          "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                        borderColor: "rgba(255, 255, 255, 1)",
                      },
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={imageSrc}
                      alt={`${brandName} ${model}`}
                      sx={{
                        objectFit: "cover",
                        bgcolor: "grey.200",
                        aspectRatio: "16 / 9",
                        transition: "transform 0.3s ease-in-out",
                      }}
                    />
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        background: "white",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        p: { xs: 2, sm: 2.5 },
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        {(brandName || model) && (
                          <>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              noWrap
                            >
                              {brandName || "-"}
                            </Typography>

                            <Typography
                              variant="h6"
                              component="p"
                              fontWeight="600"
                              noWrap
                              sx={{ textTransform: "capitalize" }}
                            >
                              {model || "-"}
                            </Typography>
                          </>
                        )}

                        {(year || modelYear) && (
                          <Typography variant="body2" color="text.secondary">
                            {year && modelYear
                              ? `${year}/${modelYear}`
                              : year || modelYear}
                          </Typography>
                        )}

                        {!!mileage && (
                          <Typography variant="body2" color="text.secondary">
                            {Number(mileage).toLocaleString("pt-BR")} km
                          </Typography>
                        )}

                        {(city || state) && (
                          <Typography variant="body2" color="text.secondary">
                            {[city, state].filter(Boolean).join(" - ")}
                          </Typography>
                        )}
                      </Box>

                      {!!askingPrice && (
                        <Box
                          sx={{
                            pt: 1.5,
                            borderTop: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "0.75rem", mb: 0.5 }}
                          >
                            Preço
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "1.5rem",
                              background:
                                "linear-gradient(135deg, #F49841 0%, #F2852A 100%)",
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                            }}
                          >
                            R${askingPrice}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Grid>
        )}
      </Container>

      <VehicleDetailsModal
        open={vehicleModalOpen}
        onClose={handleCloseVehicle}
        vehicle={selectedVehicle}
        view={false}
      />
    </Box>
  );
};

export default BuyerVitrinePage;
